import axios from 'src/utils/axios';
import parse from 'src/utils/parse';
import logger from 'src/utils/logger';
import type {
  WazuhMitreQuery,
  WazuhMitreSummaryData,
  WazuhMitreTechniqueItem,
} from 'src/types/mindtime/wazuhMitre';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';

const parseFilenameFromContentDisposition = (header: string | undefined): string | null => {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=(?:UTF-8'')?([^;\s]+)/i);
  if (utf8Match) {
    const raw = utf8Match[1].replace(/['"]/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const asciiMatch = header.match(/filename\s*=\s*("?)([^";\n]+)\1/i);
  return asciiMatch ? asciiMatch[2].trim() : null;
};

const readBlobErrorMessage = async (blob: Blob): Promise<string> => {
  try {
    const text = await blob.text();
    try {
      const parsed = JSON.parse(text) as { message?: string };
      return parsed.message || text || 'Export failed';
    } catch {
      return text || 'Export failed';
    }
  } catch {
    return 'Export failed';
  }
};

class WazuhMitreApi {
  private appendQuery(url: string, params?: Record<string, unknown>): string {
    if (!params) return url;
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    });
    const query = qs.toString();
    if (!query) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${query}`;
  }

  async getSummary(params?: WazuhScopeParams): Promise<WazuhMitreSummaryData> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/mitre/summary`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getTechniques(params: WazuhMitreQuery): Promise<WazuhPagedData<WazuhMitreTechniqueItem>> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/mitre/techniques`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data;
            if (!data || !Array.isArray(data.items)) {
              throw new Error('Invalid response: mitre/techniques must contain items array');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getTopTechniques(
    params?: WazuhScopeParams & { limit?: number }
  ): Promise<WazuhMitreTechniqueItem[]> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/mitre/top-techniques`, params))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data?.data;
            if (!data || !Array.isArray(data.items)) {
              throw new Error('Invalid response: mitre/top-techniques must contain items array');
            }
            resolve(data.items);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async export(params: WazuhMitreQuery): Promise<{ blob: Blob; filename: string }> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_WAZUH_SERVICE;
      if (!baseUrl) {
        const error = new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendQuery(`${baseUrl}/api/v1/mitre/export`, params), {
          responseType: 'blob',
          headers: { Accept: '*/*' },
        })
        .then(async (response) => {
          try {
            const blob = response.data as Blob;
            if (!blob || response.status < 200 || response.status >= 300) {
              throw new Error(await readBlobErrorMessage(blob));
            }

            const contentType = String(response.headers['content-type'] || '');
            if (contentType.includes('application/json')) {
              throw new Error(await readBlobErrorMessage(blob));
            }

            const filename =
              parseFilenameFromContentDisposition(
                response.headers['content-disposition'] as string | undefined
              ) || 'mitre_techniques_export.xlsx';

            resolve({ blob, filename });
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch(async (error) => {
          if (error?.response?.data instanceof Blob) {
            try {
              reject(new Error(await readBlobErrorMessage(error.response.data)));
              return;
            } catch {
              // fall through
            }
          }
          logger(error, 'error');
          reject(error instanceof Error ? error : new Error('Export failed'));
        });
    });
  }
}

export const mindtimeWazuhMitreApi = new WazuhMitreApi();
